import React, { useState, useEffect } from "react";
import {
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CForm,
  CFormLabel,
  CFormInput,
  CFormTextarea,
  CButton,
  CSpinner,
  CAlert,
} from "@coreui/react-pro";
import { toast } from "react-toastify";
import { recordRefund } from "../../../utils/backend";

const MYR = (v) =>
  v === null || v === undefined || v === ""
    ? "-"
    : new Intl.NumberFormat("en-MY", {
        style: "currency",
        currency: "MYR",
        minimumFractionDigits: 2,
      }).format(Number(v));

/**
 * Records an out-of-band (RHB / Revenue Monster) refund against an order.
 * Calls POST /admin/order/refund; on success refreshes the parent list.
 * Does NOT move money — it reflects a refund the Finance team already processed.
 */
const RecordRefundModal = ({ row, onClose, onSuccess }) => {
  const [amount, setAmount] = useState("");
  const [reference, setReference] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Prefill amount with what was collected (a full refund) whenever a row opens.
  useEffect(() => {
    if (row) {
      setAmount(row.amount_paid != null ? String(row.amount_paid) : "");
      setReference("");
      setReason("");
    }
  }, [row]);

  if (!row) return null;

  const amt = Number(amount);
  const paid = Number(row.amount_paid) || 0;
  const isPartial = amt > 0 && amt < paid;
  const amountInvalid = !(amt > 0) || amt > paid + 0.001;
  const invalid = amountInvalid || !reference.trim();

  const submit = async () => {
    if (invalid) return;
    setSubmitting(true);
    try {
      await recordRefund({
        order_number: row.order_number,
        refund_amount: amt,
        reference: reference.trim(),
        reason: reason.trim() || undefined,
        transaction_id: row.transaction_id || undefined,
      });
      toast.success(`Refund recorded for ${row.order_number}`);
      if (onSuccess) onSuccess();
      if (onClose) onClose();
    } catch (e) {
      toast.error(e.message || "Failed to record refund");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <CModal visible={!!row} onClose={onClose} alignment="center">
      <CModalHeader>
        <CModalTitle>Record refund</CModalTitle>
      </CModalHeader>
      <CModalBody>
        <div className="mb-3 small text-medium-emphasis">
          <div>
            <strong>{row.car_plate || "—"}</strong> · {row.customer_name || "—"}
          </div>
          <div>
            Order {row.order_number} · paid <strong>{MYR(row.amount_paid)}</strong>
            {row.transaction_id ? ` · txn ${row.transaction_id}` : ""}
          </div>
        </div>

        <CAlert color="info" className="py-2 small">
          Records a refund already processed in the RHB / Revenue Monster dashboard — sets
          this order to{" "}
          <strong>{isPartial ? "Partially Refunded" : "Refunded"}</strong> so the finance
          report nets it off. It does <strong>not</strong> move money.
        </CAlert>

        <CForm
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <div className="mb-3">
            <CFormLabel>Refund amount (MYR)</CFormLabel>
            <CFormInput
              type="number"
              step="0.01"
              min="0"
              max={paid || undefined}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              invalid={amount !== "" && amountInvalid}
            />
            <div className="form-text">
              Up to the amount paid ({MYR(row.amount_paid)}). Less than that records a
              partial refund.
            </div>
          </div>

          <div className="mb-3">
            <CFormLabel>RHB / RM refund reference</CFormLabel>
            <CFormInput
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="e.g. RHB IBG ref"
            />
          </div>

          <div className="mb-1">
            <CFormLabel>Reason (optional)</CFormLabel>
            <CFormTextarea
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. duplicate charge — 2nd successful payment"
            />
          </div>
        </CForm>
      </CModalBody>
      <CModalFooter>
        <CButton color="secondary" variant="outline" onClick={onClose} disabled={submitting}>
          Cancel
        </CButton>
        <CButton color="danger" onClick={submit} disabled={invalid || submitting}>
          {submitting ? (
            <CSpinner size="sm" />
          ) : isPartial ? (
            "Record partial refund"
          ) : (
            "Record refund"
          )}
        </CButton>
      </CModalFooter>
    </CModal>
  );
};

export default RecordRefundModal;
